import { useState } from 'react';
import { COLORS } from '../../constants/theme';

const MAX_KINGDOMS_PER_ISLAND = 50;

export interface KingdomBrowseHandlers {
  onSelect: (island: number, kingdom: number) => void;
  onPrevious: () => void;
  onNext: () => void;
  onRandom: () => void;
  previousDisabled: boolean;
  nextDisabled: boolean;
}

/**
 * SF-169 Component 2: extracted (not duplicated) from `Dossier.tsx`'s former island/kingdom
 * picker form + Previous/Next/Random buttons, lifted into a standalone shared component so both
 * `DesktopKingdomViewerShell` and `MobileKingdomViewerShell` can render it without copy-pasting
 * the form-validation and button-disabled logic.
 *
 * Accessibility fixes applied during extraction (Recommended Change #10): corrected
 * Previous/Next aria-labels (was Dossier's "Previous K"/"Next K"), and a 44×44px minimum hit
 * target on every button/input, matching MobileTabBar's existing convention.
 */
export function KingdomBrowseControls({
  onSelect,
  onPrevious,
  onNext,
  onRandom,
  previousDisabled,
  nextDisabled,
}: KingdomBrowseHandlers) {
  const [islandInput, setIslandInput] = useState('1');
  const [kingdomInput, setKingdomInput] = useState('1');
  const [navError, setNavError] = useState<string | null>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setNavError(null);

    const island = parseInt(islandInput, 10);
    const kingdomNum = parseInt(kingdomInput, 10);

    if (isNaN(island) || island < 1) {
      setNavError('Island must be a positive number');
      return;
    }
    if (isNaN(kingdomNum) || kingdomNum < 1 || kingdomNum > MAX_KINGDOMS_PER_ISLAND) {
      setNavError(`Kingdom must be between 1 and ${MAX_KINGDOMS_PER_ISLAND}`);
      return;
    }

    onSelect(island, kingdomNum);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, width: '100%' }}>
      <form onSubmit={handleSubmit} noValidate style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{ color: COLORS.text.tertiary, fontSize: 13 }}>Kingdom:</span>
          <input
            type="number"
            value={kingdomInput}
            onChange={(e) => setKingdomInput(e.target.value)}
            min={1}
            max={MAX_KINGDOMS_PER_ISLAND}
            style={{
              width: 60,
              minHeight: 44,
              padding: '4px 8px',
              background: COLORS.inputBg,
              border: `1px solid ${COLORS.inputBorder}`,
              color: COLORS.text.primary,
              fontSize: 13,
            }}
          />
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{ color: COLORS.text.tertiary, fontSize: 13 }}>Island:</span>
          <input
            type="number"
            value={islandInput}
            onChange={(e) => setIslandInput(e.target.value)}
            min={1}
            style={{
              width: 60,
              minHeight: 44,
              padding: '4px 8px',
              background: COLORS.inputBg,
              border: `1px solid ${COLORS.inputBorder}`,
              color: COLORS.text.primary,
              fontSize: 13,
            }}
          />
        </label>
        <button
          type="submit"
          style={{
            minHeight: 44,
            minWidth: 44,
            padding: '4px 12px',
            background: COLORS.btnPrimary,
            border: `1px solid ${COLORS.border}`,
            color: COLORS.text.primary,
            cursor: 'pointer',
            fontSize: 13,
          }}
        >
          View
        </button>
      </form>

      <div style={{ display: 'flex', gap: 8, width: '100%' }}>
        <button
          type="button"
          onClick={onPrevious}
          disabled={previousDisabled}
          aria-label="Previous kingdom"
          style={{
            flex: 1,
            minHeight: 44,
            minWidth: 44,
            padding: '4px 12px',
            background: COLORS.sectionHeader,
            border: `1px solid ${COLORS.border}`,
            color: previousDisabled ? COLORS.text.muted : COLORS.text.primary,
            cursor: previousDisabled ? 'not-allowed' : 'pointer',
            fontSize: 13,
          }}
        >
          Previous K
        </button>
        <button
          type="button"
          onClick={onRandom}
          aria-label="Random Kingdom"
          style={{
            flex: 1,
            minHeight: 44,
            minWidth: 44,
            padding: '4px 12px',
            background: COLORS.sectionHeader,
            border: `1px solid ${COLORS.border}`,
            color: COLORS.text.primary,
            cursor: 'pointer',
            fontSize: 13,
          }}
        >
          Random Kingdom
        </button>
        <button
          type="button"
          onClick={onNext}
          disabled={nextDisabled}
          aria-label="Next kingdom"
          style={{
            flex: 1,
            minHeight: 44,
            minWidth: 44,
            padding: '4px 12px',
            background: COLORS.sectionHeader,
            border: `1px solid ${COLORS.border}`,
            color: nextDisabled ? COLORS.text.muted : COLORS.text.primary,
            cursor: nextDisabled ? 'not-allowed' : 'pointer',
            fontSize: 13,
          }}
        >
          Next K
        </button>
      </div>

      {navError && (
        <span role="alert" style={{ color: COLORS.negative, fontSize: 12 }}>
          {navError}
        </span>
      )}
    </div>
  );
}
